import React, { useContext, useReducer, useState, useEffect } from 'react';
import api from '../api';
import { Store } from '../Store';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import { PASSWORD_PATTERN } from '../utils/passwordRule';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import Badge from 'react-bootstrap/Badge';
import LoadingLogo from '../components/general/LoadingLogo';
import { profileUpdates } from '../utils/accountUpdates';

const reducer = (state, action) => {
  // reducer function to update the state for different cases
  switch (action.type) {
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    default:
      return state;
  }
};

export default function ProfileScreen() {
  const { t, i18n } = useTranslation();
  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives access to the state of the context (in this case the Store)
  const { userInfo } = state; // gets the userInfo out of the application state

  const loading = false; // placeholder variable for component loading status

  const [username, setUsername] = useState(userInfo.username);
  const [email, setEmail] = useState(userInfo.email);
  const [tempEmail, setTempEmail] = useState(userInfo.tempEmail || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isResending, setIsResending] = useState(false); // State variable to manage resend button disabled state

  // Marketing preferences state
  const [receiveMarketingEmails, setReceiveMarketingEmails] = useState(
    userInfo.marketingPreferences?.receiveMarketingEmails || false
  );
  const [newProductArrivals, setNewProductArrivals] = useState(
    userInfo.marketingPreferences?.newProductArrivals || false
  );
  const [restockAnnouncements, setRestockAnnouncements] = useState(
    userInfo.marketingPreferences?.restockAnnouncements || false
  );
  const [eventsAndDiscounts, setEventsAndDiscounts] = useState(
    userInfo.marketingPreferences?.eventsAndDiscounts || false
  );
  const [companyNews, setCompanyNews] = useState(
    userInfo.marketingPreferences?.companyNews || false
  );

  const [{ loadingUpdate }, dispatch] = useReducer(reducer, {
    // useReducer function, that gets the states from the reducer function and dispatch, to dispatch actions to the reducer (replaces the useState hook, to manage complex state)
    loadingUpdate: false,
  });

  useEffect(() => {
    setUsername(userInfo.username);
    setEmail(userInfo.email);
    setTempEmail(userInfo.tempEmail || '');
    setReceiveMarketingEmails(
      userInfo.marketingPreferences?.receiveMarketingEmails || false
    );
    setNewProductArrivals(
      userInfo.marketingPreferences?.newProductArrivals || false
    );
    setRestockAnnouncements(
      userInfo.marketingPreferences?.restockAnnouncements || false
    );
    setEventsAndDiscounts(
      userInfo.marketingPreferences?.eventsAndDiscounts || false
    );
    setCompanyNews(userInfo.marketingPreferences?.companyNews || false);
  }, [userInfo]); // Ensure the component re-renders when userInfo changes

  const submitHandler = async (e) => {
    e.preventDefault(); // prevents a refresh of the whole page, when the button is clicked

    if (newPassword !== confirmNewPassword) {
      toast.error(t('profile-screen-error-1'));
      return;
    } // checks if the newPassword and confirmeNewPassword match

    const updatedData = profileUpdates({
      userInfo,
      username,
      email,
      newPassword,
      marketingPreferences: {
        receiveMarketingEmails,
        newProductArrivals,
        restockAnnouncements,
        eventsAndDiscounts,
        companyNews,
      },
    }); // only the changed fields, and the marketing preferences as a whole once one changed

    if (Object.keys(updatedData).length === 0) {
      return; // exits the submitHandler, if there have been no changes in username, email, or password
    }

    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      updatedData.baseUrl = window.location.origin; // Add baseUrl to the updatedData (after checking, if the username, email or password has changed)
      updatedData.language = i18n.language; // Add the current language

      const { data } = await api.patch(
        '/api/users/profile',
        updatedData // the data the user wants to update
      ); // an axios put request, that updates the existing data, at the /api/users/profile api, in the backend
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      ctxDispatch({ type: 'USER_SIGNIN', payload: data }); // updates the userInfo in the applications state
      localStorage.setItem('userInfo', JSON.stringify(data)); // updates the userInfo in the users local storage
      toast.success(t('profile-screen-success-1'));
    } catch (err) {
      setUsername(userInfo.username);
      setEmail(userInfo.email);
      setTempEmail(userInfo.tempEmail || '');

      dispatch({
        type: 'UPDATE_FAIL',
      });
      toast.error(t(getError(err))); // using the error message as the key for the translation json files. If the key doesn't exist, the message itself will be displayed inside the toest.error
    }
  };

  const resendVerificationEmail = async () => {
    setIsResending(true); // Disable the button when the function starts
    try {
      const baseUrl = window.location.origin; // Get the base URL
      const currentLanguage = i18n.language; // Gets the current language
      await api.post('/api/users/resend-verification-email', {
        baseUrl,
        language: currentLanguage,
      });
      toast.success(t('profile-screen-email-resent'));
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setIsResending(false); // Re-enable the button when the function completes
    }
  };

  const withdrawEmailRequest = async () => {
    try {
      const { data } = await api.post('/api/users/withdraw-email-request', {});
      ctxDispatch({ type: 'USER_SIGNIN', payload: data });
      localStorage.setItem('userInfo', JSON.stringify(data));
      setTempEmail('');
      toast.success(t('profile-screen-email-update-withdrawn'));
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const handleReceiveMarketingEmailsChange = (e) => {
    setReceiveMarketingEmails(e.target.value === 'yes');
    if (e.target.value === 'yes') {
      setNewProductArrivals(true);
      setRestockAnnouncements(true);
      setEventsAndDiscounts(true);
      setCompanyNews(true);
    } else {
      setNewProductArrivals(false);
      setRestockAnnouncements(false);
      setEventsAndDiscounts(false);
      setCompanyNews(false);
    }
  };

  return (
    <Container>
      <div className="container small-container">
        <title>{t('profile-screen-helmet')}</title>

        <h1 className="screen-heading">{t('profile-screen-heading')}</h1>

        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : (
          <form onSubmit={submitHandler}>
            <Form.Group className="profile-username" controlId="username">
              <Form.Label>{t('profile-screen-username')}</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={username}
                disabled={loadingUpdate}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="profile-email" controlId="email">
              <Form.Label>{t('profile-screen-email')}</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="email"
                value={email}
                disabled={loadingUpdate}
                onChange={(e) => setEmail(e.target.value)}
              />

              {tempEmail && (
                <Form.Text className="temp-email-container">
                  <OverlayTrigger
                    trigger={['click', 'hover']}
                    rootClose // close the popover when the user clicks outside of it
                    placement="bottom"
                    overlay={
                      <Popover id="popover-password">
                        <Popover.Header as="h3">
                          {t(
                            'profile-screen-email-verification-popover-heading'
                          )}
                        </Popover.Header>
                        <Popover.Body>
                          <h5>
                            {t('profile-screen-email-verification-popover')}
                          </h5>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <Badge
                      className="temp-email-badge"
                      bg="info"
                      tabIndex="0"
                      role="button"
                    >
                      <div className="temp-email-badge-text">
                        <span className="badge-verification">
                          {t('profile-screen-verification')}{' '}
                        </span>
                        {t('profile-screen-pending')}
                      </div>
                      <div className="temp-email-badge-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="currentColor"
                            d="M22 5.5H9c-1.1 0-2 .9-2 2v9a2 2 0 0 0 2 2h13c1.11 0 2-.89 2-2v-9a2 2 0 0 0-2-2m0 11H9V9.17l6.5 3.33L22 9.17zm-6.5-5.69L9 7.5h13zM5 16.5c0 .17.03.33.05.5H1c-.552 0-1-.45-1-1s.448-1 1-1h4zM3 7h2.05c-.02.17-.05.33-.05.5V9H3c-.55 0-1-.45-1-1s.45-1 1-1m-2 5c0-.55.45-1 1-1h3v2H2c-.55 0-1-.45-1-1"
                          />
                        </svg>
                      </div>
                    </Badge>
                  </OverlayTrigger>

                  <span className="temp-email">{tempEmail}</span>

                  <Button
                    variant="light"
                    className="temp-email-resend-button"
                    onClick={resendVerificationEmail}
                    disabled={isResending || loadingUpdate} // Disable the button when resending
                  >
                    <span className="resend-button-text">
                      {t('profile-screen-resend')}
                    </span>
                    <div className="resend-button-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="currentColor"
                          d="M3 4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h10.5a6.5 6.5 0 0 1-.5-2H3V8l8 5l8-5v3a7 7 0 0 1 .5 0a6.5 6.5 0 0 1 1.5.18V6c0-1.1-.9-2-2-2zm0 2h16l-8 5zm16 6l-2.25 2.25L19 16.5V15a2.5 2.5 0 0 1 2.5 2.5c0 .4-.09.78-.26 1.12l1.09 1.09c.42-.63.67-1.39.67-2.21c0-2.21-1.79-4-4-4zm-3.33 3.29c-.42.63-.67 1.39-.67 2.21c0 2.21 1.79 4 4 4V23l2.25-2.25L19 18.5V20a2.5 2.5 0 0 1-2.5-2.5c0-.4.09-.78.26-1.12z"
                        />
                      </svg>
                    </div>
                  </Button>

                  <Button
                    variant="light"
                    className="temp-email-withdraw-button"
                    onClick={withdrawEmailRequest}
                    disabled={loadingUpdate}
                  >
                    <svg
                      fill="currentColor"
                      viewBox="0 0 56 56"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path d="M 44.5235 48.6602 L 46.1407 14.3945 L 48.4844 14.3945 C 49.4454 14.3945 50.2187 13.5976 50.2187 12.6367 C 50.2187 11.6758 49.4454 10.8555 48.4844 10.8555 L 38.2422 10.8555 L 38.2422 7.3398 C 38.2422 3.9883 35.9688 1.8086 32.3595 1.8086 L 23.5938 1.8086 C 19.9844 1.8086 17.7344 3.9883 17.7344 7.3398 L 17.7344 10.8555 L 7.5391 10.8555 C 6.6016 10.8555 5.7813 11.6758 5.7813 12.6367 C 5.7813 13.5976 6.6016 14.3945 7.5391 14.3945 L 9.8829 14.3945 L 11.5000 48.6836 C 11.6641 52.0586 13.8907 54.1914 17.2657 54.1914 L 38.7579 54.1914 C 42.1095 54.1914 44.3595 52.0351 44.5235 48.6602 Z M 21.4844 7.5742 C 21.4844 6.2383 22.4688 5.3008 23.8751 5.3008 L 32.1016 5.3008 C 33.5313 5.3008 34.5157 6.2383 34.5157 7.5742 L 34.5157 10.8555 L 21.4844 10.8555 Z M 17.6173 50.6758 C 16.2579 50.6758 15.2500 49.6445 15.1797 48.2852 L 13.5391 14.3945 L 42.3907 14.3945 L 40.8438 48.2852 C 40.7735 49.6680 39.7891 50.6758 38.4063 50.6758 Z M 34.9610 46.5508 C 35.7344 46.5508 36.3204 45.9180 36.3438 45.0273 L 37.0469 20.2773 C 37.0704 19.3867 36.4610 18.7305 35.6641 18.7305 C 34.9376 18.7305 34.3282 19.4102 34.3048 20.2539 L 33.6016 45.0273 C 33.5782 45.8711 34.1641 46.5508 34.9610 46.5508 Z M 21.0626 46.5508 C 21.8595 46.5508 22.4454 45.8711 22.4219 45.0273 L 21.7188 20.2539 C 21.6954 19.4102 21.0626 18.7305 20.3360 18.7305 C 19.5391 18.7305 18.9532 19.3867 18.9766 20.2773 L 19.7032 45.0273 C 19.7266 45.9180 20.2891 46.5508 21.0626 46.5508 Z M 29.4298 45.0273 L 29.4298 20.2539 C 29.4298 19.4102 28.7969 18.7305 28.0235 18.7305 C 27.2500 18.7305 26.5938 19.4102 26.5938 20.2539 L 26.5938 45.0273 C 26.5938 45.8711 27.2500 46.5508 28.0235 46.5508 C 28.7735 46.5508 29.4298 45.8711 29.4298 45.0273 Z"></path>
                      </g>
                    </svg>
                  </Button>
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="profile-password" controlId="newPassword">
              <Form.Label>
                <div className="display-inline">
                  {t('profile-screen-new-password-1')}
                  <OverlayTrigger
                    trigger="click"
                    rootClose // close the popover when the user clicks outside of it
                    placement="bottom"
                    overlay={
                      <Popover id="popover-password">
                        <Popover.Header as="h3">
                          {t('profile-screen-popover-heading')}
                        </Popover.Header>
                        <Popover.Body>
                          <ul>
                            <li>{t('profile-screen-popover-li-1')}</li>
                            <li>{t('profile-screen-popover-li-2')}</li>
                            <li>{t('profile-screen-popover-li-3')}</li>
                            <li>{t('profile-screen-popover-li-4')}</li>
                            <li>{t('profile-screen-popover-li-5')}</li>
                          </ul>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <Button className="info-popover">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#707070"
                        className="bi bi-info-circle"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                      </svg>
                    </Button>
                  </OverlayTrigger>
                </div>
              </Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type={passwordVisible ? 'text' : 'password'}
                disabled={loadingUpdate}
                pattern={PASSWORD_PATTERN}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Group>

            <Form.Group
              className="profile-confirm-password"
              controlId="confirmNewPassword"
            >
              <Form.Label>{t('profile-screen-new-password-2')}</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type={passwordVisible ? 'text' : 'password'}
                disabled={loadingUpdate}
                pattern={PASSWORD_PATTERN}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </Form.Group>

            <Form.Group
              className="profile-show-password show-password"
              controlId="passwordVisibility"
            >
              <Form.Check
                className="view-password"
                type="checkbox"
                label={t('profile-screen-show-password')}
                disabled={loadingUpdate}
                onChange={() => setPasswordVisible(!passwordVisible)}
                checked={passwordVisible}
              />
            </Form.Group>

            <div>
              <Form.Group
                controlId="receiveMarketingEmails"
                className="profile-marketing-emails"
              >
                <Form.Label>
                  {t('profile-screen-receive-marketing-emails')}
                </Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  value={receiveMarketingEmails ? 'yes' : 'no'}
                  disabled={loadingUpdate}
                  onChange={handleReceiveMarketingEmailsChange}
                >
                  <option value="yes">{t('profile-screen-yes')}</option>
                  <option value="no">{t('profile-screen-no')}</option>
                </Form.Select>
              </Form.Group>

              {receiveMarketingEmails && (
                <div>
                  <Form.Group
                    className="profile-marketing-email-checkbox"
                    controlId="eventsAndDiscounts"
                  >
                    <Form.Check
                      type="checkbox"
                      label={t('profile-screen-events-and-discounts')}
                      checked={eventsAndDiscounts}
                      disabled={loadingUpdate}
                      onChange={(e) => setEventsAndDiscounts(e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group
                    className="profile-marketing-email-checkbox"
                    controlId="newProductArrivals"
                  >
                    <Form.Check
                      type="checkbox"
                      label={t('profile-screen-product-arrivals')}
                      checked={newProductArrivals}
                      disabled={loadingUpdate}
                      onChange={(e) => setNewProductArrivals(e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group
                    className="profile-marketing-email-checkbox"
                    controlId="restockAnnouncements"
                  >
                    <Form.Check
                      type="checkbox"
                      label={t('profile-screen-restock-announcements')}
                      checked={restockAnnouncements}
                      disabled={loadingUpdate}
                      onChange={(e) =>
                        setRestockAnnouncements(e.target.checked)
                      }
                    />
                  </Form.Group>
                  <Form.Group
                    className="profile-marketing-email-checkbox"
                    controlId="companyNews"
                  >
                    <Form.Check
                      type="checkbox"
                      label={t('profile-screen-company-news')}
                      checked={companyNews}
                      disabled={loadingUpdate}
                      onChange={(e) => setCompanyNews(e.target.checked)}
                    />
                  </Form.Group>
                </div>
              )}
            </div>

            <div className="mt-5 text-center">
              <Button
                variant="secondary"
                type="submit"
                disabled={loadingUpdate}
              >
                {t('profile-screen-save')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Container>
  );
}
